import { useUserSelectUsersQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from '../../hooks/useOptions'
import { UserSelectContext } from './types'
import { useUserSelectTooltip } from './hooks/useUserSelectTooltip'
import { useUserSelectLabel } from './hooks/useUserSelectLabel'
import { useUserDeleted } from 'hooks/useUserDeleted'
import { createStrictContext } from 'utils/createContext'
import { toLabeledValues } from '../../utils/labeledValues'
import { alphabeticalCompare } from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<UserSelectContext>('UserSelectContext')

/**
 * returns the context associated with the {@link UserSelectProvider}
 *
 * @throws if the UserSelectProvider is not found
 */
export const useUserSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the UserSelect
 *
 * @param props
 */
const UserSelectProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const { data, loading } = useUserSelectUsersQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const label = useUserSelectLabel()
  const tooltip = useUserSelectTooltip()
  const deleted = useUserDeleted()
  const users = data?.project?.users ?? []
  const options = useOptions({
    items: users,
    value: 'id',
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })
  return (
    <Provider
      value={{
        loading,
        options,
        users,
        fromLabeledValues: (values) =>
          (values ?? []).map(({ value }) => value as string),
        fromLabeledValue: (value) => value?.value as string,
        toLabeledValues: (records) =>
          toLabeledValues(records, { value: 'id', label, tooltip, deleted }),
        toLabeledValue: (record) =>
          record
            ? toLabeledValues([record], {
                value: 'id',
                label,
                tooltip,
                deleted,
              })[0]
            : undefined,
      }}
    >
      {children}
    </Provider>
  )
}

export default UserSelectProvider
